import { useMutation } from '@apollo/client';
import { AnalyticIdentifier } from '@common/neo.tax';
import * as Sentry from '@sentry/react';
import { Button, GeneralFormError, AlertModalLayout, ControlledInput, Modal } from '@ui-components/neo.tax';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  DELETE_ORGANIZATION_INVITATION,
  DeleteOrganizationInvitationMutation,
  DeleteOrganizationInvitationMutationVariables,
} from '@neo.tax/shared/graphql';
import { useAnalyticId } from '@neo.tax/shared/state';
import { OrganizationMembersTableItemInvitation } from '@neo.tax/shared/types';

import { DeleteInvitedMemberFormValues } from './delete-invited-member.types';

interface DeleteInvitedMemberModalProps {
  setIsOpen: (isOpen: boolean) => void;
  isOpen: boolean;
  invitation: OrganizationMembersTableItemInvitation | undefined;
  onDelete: () => void;
}

export const DeleteInvitedMemberModal = ({
  invitation,
  isOpen,
  onDelete,
  setIsOpen,
}: DeleteInvitedMemberModalProps): React.ReactElement | null => {
  const emailInputId = useAnalyticId(AnalyticIdentifier.RTC_DELETE_INVITED_MEMBER_EMAIL_INPUT);
  const [errorMessage, setErrorMessage] = useState<string>();

  const { control, formState, handleSubmit, reset } = useForm<DeleteInvitedMemberFormValues>({
    mode: 'onChange',
    defaultValues: {
      email: '',
    },
  });

  const [deleteOrganizationInvitation, { loading: deleteOrganizationInvitationLoading }] = useMutation<
    DeleteOrganizationInvitationMutation,
    DeleteOrganizationInvitationMutationVariables
  >(DELETE_ORGANIZATION_INVITATION);

  const onSubmit = async (): Promise<void> => {
    if (!invitation) {
      return;
    }
    const invitationId = invitation.id;
    try {
      await deleteOrganizationInvitation({
        variables: { organizationInvitationId: invitationId },
        update: (cache) => {
          cache.modify({
            id: `OrganizationInvitation:${invitation.id}`,
            fields: (_, { DELETE }) => DELETE,
          });
        },
        onCompleted: onDelete,
      });
      setIsOpen(false);
    } catch (e) {
      setErrorMessage('Something went wrong. Please try again.');
      Sentry.captureException(e);
    }
  };

  const onClickCancel = (): void => {
    setIsOpen(false);
    reset();
  };

  if (!invitation) return null;
  return (
    <Modal isOpen={!!isOpen} setIsOpen={setIsOpen}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <AlertModalLayout
          title={`Delete invitation for "${invitation.email}"`}
          copy={`Are you sure you want to revoke the invitation for ${invitation.email}?`}
          trailingContent={
            <>
              <Button size="base" variant="white" rounded="regular" onClick={onClickCancel}>
                Cancel
              </Button>
              <Button
                disabled={!formState.isValid || deleteOrganizationInvitationLoading}
                rounded="regular"
                size="base"
                type="submit"
                variant="rsbry"
              >
                {deleteOrganizationInvitationLoading ? 'Deleting Member...' : 'Delete'}
              </Button>
            </>
          }
        >
          <ControlledInput
            control={control}
            name="email"
            rules={{ required: true, validate: (email: string) => email === invitation.email }}
            id={emailInputId}
            label="To delete, please type the email:"
          />
          <GeneralFormError centered error={!!errorMessage}>
            {errorMessage}
          </GeneralFormError>
        </AlertModalLayout>
      </form>
    </Modal>
  );
};
